import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
// import { Phone } from "lucide-react"; // Commented out - phone login disabled
import "./authStyles.css";
import bgi from "../assets/Apricverseuse.jpeg";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const getDashboardPath = (userRole) => {
    if (!userRole) return '/farmer-dashboard';
    const role = userRole.toLowerCase();
    switch (role) {
      case 'expert':
        return '/expert-dashboard';
      case 'retailer':
        return '/retailer-dashboard';
      case 'farmer':
      default:
        return '/farmer-dashboard';
    }
  };
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // const [usePhoneNumber, setUsePhoneNumber] = useState(false); // Commented out - using email only for now
  const usePhoneNumber = false; // Always false - email only
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null); // Local error state
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    email: '',
    // phone: '', // Commented out - using email only for now
    password: ''
  });

  // Clear any existing errors when component mounts (but keep them if they exist from previous attempt)
  useEffect(() => {
    // Only clear errors on initial mount, not on every render
    // This allows errors to persist after failed login attempts
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Helper function to parse error messages and map to fields
  const parseErrorToFields = (errorMessage) => {
    const fieldMap = {
      'email': ['email', 'Email', 'EMAIL', 'email address', 'Email address'],
      'phone': ['phone', 'Phone', 'PHONE', 'phone number', 'Phone number'],
      'password': ['password', 'Password', 'PASSWORD', 'incorrect password', 'wrong password'],
    };

    const errors = {};
    const lowerMessage = errorMessage.toLowerCase();

    // Check if it's a validation error with multiple fields
    if (lowerMessage.includes('validation') || lowerMessage.includes('required') || lowerMessage.includes('invalid')) {
      // Try to extract specific field errors from the message
      for (const [field, keywords] of Object.entries(fieldMap)) {
        for (const keyword of keywords) {
          if (lowerMessage.includes(keyword.toLowerCase())) {
            // Create a more specific error message for this field
            if (lowerMessage.includes('required')) {
              errors[field] = `${keywords[0]} is required`;
            } else if (lowerMessage.includes('invalid')) {
              errors[field] = `Please enter a valid ${keywords[0].toLowerCase()}`;
            } else {
              errors[field] = errorMessage;
            }
          }
        }
      }
      
      // If we found field-specific errors, return them
      if (Object.keys(errors).length > 0) {
        return errors;
      }
    }

    // Check for single field errors
    for (const [field, keywords] of Object.entries(fieldMap)) {
      for (const keyword of keywords) {
        if (lowerMessage.includes(keyword.toLowerCase())) {
          errors[field] = errorMessage;
          return errors;
        }
      }
    }

    // Special cases for login errors
    if (lowerMessage.includes('invalid credentials') || lowerMessage.includes('incorrect login') || lowerMessage.includes('authentication failed') || lowerMessage.includes('invalid email') || lowerMessage.includes('incorrect password')) {
      // For authentication failures, show a clear global error message
      return {}; // Return empty to show global error
    }

    // User not found errors
    if (lowerMessage.includes('user not found') || lowerMessage.includes('account not found')) {
      errors['email'] = 'No account found with this email address';
      return errors;
    }

    // If no specific field is identified, return empty object (will show global error)
    return {};
  };

  // Helper function to handle API errors that might be structured or string-based
  const handleApiError = (apiResult) => {
    // If API returns structured validation errors
    if (apiResult.errors && typeof apiResult.errors === 'object') {
      const mappedErrors = {};
      
      // Map API field names to our form field names
      const fieldMapping = {
        'email': 'email',
        'phone': 'phone',
        'password': 'password'
      };

      for (const [apiField, message] of Object.entries(apiResult.errors)) {
        const formField = fieldMapping[apiField] || apiField;
        const errorMessage = Array.isArray(message) ? message[0] : message;
        mappedErrors[formField] = errorMessage;
      }

      if (Object.keys(mappedErrors).length > 0) {
        setFieldErrors(mappedErrors);
        setError(null);
        return;
      }
    }

    // Fall back to parsing the main message
    handleError(apiResult.message || apiResult);
  };

  // Helper function to set field-specific or global error
  const handleError = (errorMessage) => {
    const fieldErrors = parseErrorToFields(errorMessage);
    
    if (Object.keys(fieldErrors).length > 0) {
      setFieldErrors(fieldErrors);
      setError(null);
    } else {
      setError(errorMessage);
      setFieldErrors({});
    }
  };

  // Commented out - phone number login disabled
  // const toggleLoginMethod = () => {
  //   setUsePhoneNumber(!usePhoneNumber);
  //   setError(null); // Clear local error when switching login method
  //   setFieldErrors({}); // Clear field errors when switching login method
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent any form submission bubbling
    
    setIsLoading(true);
    setError(null); // Clear local error
    setFieldErrors({}); // Clear field errors
    
    try {
      // Prepare credentials - email only (phone login commented out)
      const credentials = { 
        email: formData.email, 
        password: formData.password 
      };
      
      const result = await login(credentials);
      
      if (result.success) {
        // Check if user came from marketplace/checkout
        const from = location.state?.from;
        const action = location.state?.action;
        
        if (from === '/marketplace' || from === '/marketplace/cart') {
          // Redirect back to marketplace or cart
          if (action === 'checkout' || from === '/marketplace/cart') {
            navigate('/marketplace/cart');
          } else {
            navigate('/marketplace');
          }
        } else if (location.state?.from) {
          // Redirect to the page they came from
          navigate(location.state.from);
        } else {
          // Default: go to dashboard
          const dashboardPath = getDashboardPath(result.user?.role);
          navigate(dashboardPath);
        }
      } else {
        // Use new API error handling
        handleApiError(result);
        // Ensure loading is set to false so form can be resubmitted
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      handleError('An unexpected error occurred');
      setIsLoading(false);
    }
  };

  const Loader = () => (
    <div className="loadert">
      <div className="spinnert"></div>
    </div>
  );

  const FieldError = ({ fieldName }) => {
    if (!fieldErrors[fieldName]) return null;
    
    return (
      <div className="field-error">
        {fieldErrors[fieldName]}
      </div>
    );
  };

  return (
    <div className="auth-container">
      <div className="auth-image">
        <img src={bgi} alt="Agriculture imagery" />
        <div className="absolute-logo">
         <img
            src="/AGRICVERSE_DARK.png"
            alt="AgricVerse Logo"
            style={{ height: 60, cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
      </div>
      <div className="auth-form-container">
        <div className="auth-form-wrapper">
          <div className="form-section active">
            <div className="form-header">
              <h1 className="form-title">Welcome Back</h1>
              <p className="form-subtitle">
                Login With Email
              </p>
            </div>

            {/* Error Display - Simple text instead of alert-style */}
            {(error || Object.keys(fieldErrors).length > 0) && (
              <div className="error-text" style={{
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                color: '#991b1b',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {error || Object.values(fieldErrors).filter(Boolean)[0] || 'Invalid credentials. Please check your email and password.'}
              </div>
            )}

            <form className="auth-form" onSubmit={handleSubmit}>
              {/* Email login only - phone number login commented out */}
              <div className="form-group">
                <label htmlFor="login-email">Email Address</label>
                <div className="input-group">
                  <Mail size={18} />
                  <input
                    type="email"
                    id="login-email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    required
                    disabled={isLoading}
                  />
                </div>
                <FieldError fieldName="email" />
              </div>
              
              {/* Phone number login commented out - using email only for now */}
              {/* {usePhoneNumber ? (
                <div className="form-group">
                  <label htmlFor="login-phone">Phone Number</label>
                  <div className="input-group">
                    <Phone size={18} />
                    <input
                      type="tel"
                      id="login-phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <FieldError fieldName="phone" />
                </div>
              ) : null} */}

              <div className="form-group">
                <label htmlFor="login-password">Password</label>
                <div className="input-group">
                  <Lock size={18} />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="login-password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <FieldError fieldName="password" />
              </div>
              
              {/* Phone number toggle commented out - using email only for now */}
              {/* <div className="choice-phone">
                <div className="span-div">
                  <p>or</p>
                </div>

                <button
                  type="button"
                  className="toggle-login-method"
                  onClick={toggleLoginMethod}
                  disabled={isLoading}
                >
                  {usePhoneNumber ? "Use Email Instead" : "Use Phone Number"}
                </button>
              </div> */}

              <div className="login-options">
                <div className="remember-me">
                  <input
                    type="checkbox"
                    id="keep-signed-in"
                    checked={keepSignedIn}
                    onChange={() => setKeepSignedIn(!keepSignedIn)}
                    disabled={isLoading}
                  />
                  <label htmlFor="keep-signed-in">Keep me signed in</label>
                </div>

                <div className="forgot-password">
                  <button 
                    type="button" 
                    className="link-button"
                    disabled={isLoading}
                    onClick={() => navigate('/forgot-password')}
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>

              <button 
                type="submit" 
                className="primary-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    Logging in <Loader />
                  </>
                ) : (
                  "Login"
                )}
              </button>
            </form>

            <div className="form-footer">
              <p>
                Don't have an account?{" "}
                <button
                  onClick={() => navigate("/signup")}
                  className="link-button"
                  disabled={isLoading}
                >
                  Sign Up
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}